import { styled } from '@mui/material';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import { Dialog } from 'shell/components/Dialog';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const VARIANTS = ['primary', 'destructive'];

const AlertDialog = forwardRef(
    (
        {
            message,
            description,
            note = '',
            open,
            variant = 'primary',
            actionButtonText: _actionButtonText,
            cancelButtonText: _cancelButtonText,
            onAction,
            onCancel,
            onClose,
            ...rest
        },
        ref
    ) => {
        const { t } = useTranslation();

        const actionButtonText = _actionButtonText ?? t('common:controls.action');
        const cancelButtonText = _cancelButtonText ?? t('common:controls.cancel');

        if (!VARIANTS.includes(variant)) {
            throw new Error(`Shell: Invalid variant "${variant}" in component AlertDialog.`);
        }

        return (
            <CustomDialog aria-labelledby={'dialog-title'} ref={ref} open={open} onClose={onClose} {...rest}>
                <Stack alignItems={'center'}>
                    <CustomIcon kind={'error'} variant={variant} />
                    <Text
                        id={'dialog-title'}
                        align={'center'}
                        color={variant === 'primary' ? 'primary' : 'error'}
                        component={'h1'}
                        weight={'semibold'}
                    >
                        {message}
                    </Text>
                    <DescriptionText align={'center'} component={typeof description === 'string' ? 'p' : 'div'}>
                        {description}
                    </DescriptionText>
                    {note && (
                        <NoteText color={'light'} variant={'p3'}>
                            {note}
                        </NoteText>
                    )}
                    <CustomBox>
                        {cancelButtonText && (
                            <Button color={'neutral'} variant={'tertiary'} onClick={onCancel}>
                                {cancelButtonText}
                            </Button>
                        )}
                        <Button
                            data-cy={'alert-dialog-action-button'}
                            color={variant}
                            variant={'secondary'}
                            onClick={onAction}
                        >
                            {actionButtonText}
                        </Button>
                    </CustomBox>
                </Stack>
            </CustomDialog>
        );
    }
);

const CustomDialog = styled(Dialog)(({ theme }) => {
    return {
        zIndex: theme.zIndex.modal + 1,
        '& .MuiDialog-paper': {
            borderRadius: '12px',
            margin: 0,
            width: '100%',
            maxWidth: '280px',
            '& > .MuiBox-root': {
                padding: theme.spacing(5, 6),
            },
        },
    };
});

const CustomIcon = styled(Icon)(({ variant, theme }) => {
    const background = {
        primary: theme.palette.ensightful.primary[500],
        destructive: theme.palette.ensightful.error[600],
    }[variant];

    return {
        borderRadius: '50%',
        marginBottom: theme.spacing(2),
        padding: '0.3125rem',
        fontSize: '1.5rem',
        background,
        color: theme.palette.ensightful.shades[0],
    };
});

const DescriptionText = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const NoteText = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const CustomBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(6),
    marginTop: theme.spacing(3),
}));

export default AlertDialog;
